import React from "react"

import { Checkbox, Tooltip } from "@coval/ui"

import type { RunRowLike } from "@/types/RunRow"
import { cn } from "@/utils/cn"

import {
  TAG_UNTAGGED_KEY,
  type CompareBy,
  type CompareByDimension,
} from "../../../compare-by"
import type { RunResultsMetricRenderAdaptersById } from "../../types"

interface GroupLabelResolvers {
  agentsById: Record<string, { display_name: string }> | null
  personasById: Record<string, { display_name: string }> | null
  runsById: Record<string, string> | null
}

interface GroupedMetricColumn {
  key: string
}

interface GroupedAggregateRowProps {
  customDimensionGroupBySimId?: Record<string, string>
  customDimensionGroupColorById?: Record<string, string>
  customDimensionGroupNameById?: Record<string, string>
  explanationColumnCount: number
  groupCompareBy: CompareBy
  groupKey: string
  groupSelectionState: "all" | "none" | "partial"
  indentClass: string
  isExpanded: boolean
  labelResolvers: GroupLabelResolvers
  metricColumns: GroupedMetricColumn[]
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  mode: "avg" | "median" | "p95" | "min" | "max"
  onToggle: () => void
  onToggleSelection: (rows: RunRowLike[]) => void
  rows: RunRowLike[]
  selectionEnabled: boolean
}

function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return hex
  }
  const red = parseInt(hex.slice(1, 3), 16)
  const green = parseInt(hex.slice(3, 5), 16)
  const blue = parseInt(hex.slice(5, 7), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function resolveGroupLabel(
  dimension: CompareByDimension,
  groupKey: string,
  rows: RunRowLike[],
  resolvers: GroupLabelResolvers,
  displayLabels?: Record<string, string>,
): { label: string; tooltip: string | null } {
  if (dimension === "custom") {
    return { label: displayLabels?.[groupKey] ?? groupKey, tooltip: null }
  }
  if (dimension === "run") {
    const name = resolvers.runsById?.[groupKey]
    if (name) {
      return { label: name, tooltip: null }
    }
  } else if (dimension === "agent") {
    const name = resolvers.agentsById?.[groupKey]?.display_name
    if (name) {
      return { label: name, tooltip: null }
    }
  } else if (dimension === "persona") {
    const name = resolvers.personasById?.[groupKey]?.display_name
    if (name) {
      return { label: name, tooltip: null }
    }
  } else if (dimension === "mutation") {
    const name = rows.find((row) => row.mutation_name?.trim())?.mutation_name
    if (name) {
      return { label: name, tooltip: null }
    }
  } else if (dimension === "test_case") {
    const row = rows.find(
      (candidate) =>
        Boolean(candidate.test_case_description?.trim()) ||
        Boolean(candidate.input_str?.trim()),
    )
    if (row) {
      const full =
        row.test_case_description?.trim() || row.input_str?.trim() || ""
      return {
        label: full.length <= 60 ? full : `${full.slice(0, 60)}…`,
        tooltip: full,
      }
    }
  } else if (dimension === "tag") {
    // Tag keys are just the tag string; the untagged sentinel resolves to
    // a human "Untagged" label so grouped rows read cleanly.
    return {
      label: groupKey === TAG_UNTAGGED_KEY ? "Untagged" : groupKey,
      tooltip: null,
    }
  }
  return { label: groupKey, tooltip: null }
}

export function GroupedAggregateRow({
  customDimensionGroupBySimId,
  customDimensionGroupColorById,
  customDimensionGroupNameById,
  explanationColumnCount,
  groupCompareBy,
  groupKey,
  groupSelectionState,
  indentClass,
  isExpanded,
  labelResolvers,
  metricColumns,
  metricRenderAdaptersById,
  mode,
  onToggle,
  onToggleSelection,
  rows,
  selectionEnabled,
}: GroupedAggregateRowProps) {
  const groupColor = groupCompareBy.colors?.[groupKey]
  const { label, tooltip } = resolveGroupLabel(
    groupCompareBy.dimension,
    groupKey,
    rows,
    labelResolvers,
    groupCompareBy.displayLabels,
  )
  const labelNode = (
    <span
      className="font-medium text-coval-text-muted"
      style={groupColor ? { color: groupColor } : undefined}
    >
      {label}
    </span>
  )
  const countByGroupId = new Map<string | null, number>()
  if (customDimensionGroupBySimId) {
    for (const row of rows) {
      const simulationOutputId = row.simulation_output_id
      const groupId =
        typeof simulationOutputId === "string"
          ? (customDimensionGroupBySimId[simulationOutputId] ?? null)
          : null
      countByGroupId.set(groupId, (countByGroupId.get(groupId) ?? 0) + 1)
    }
  }
  const assignmentChips = Array.from(countByGroupId.entries())
    .filter(([, count]) => count > 0)
    .sort(([first], [second]) => {
      if (first === null) {
        return 1
      }
      if (second === null) {
        return -1
      }
      return 0
    })

  return (
    <tr
      className={`border-b border-coval-border cursor-pointer hover:bg-surface-0 ${
        isExpanded ? "bg-surface-0" : ""
      }`}
      onClick={onToggle}
    >
      {selectionEnabled && (
        <td
          className={`text-sm py-2 px-3 align-middle w-10 ${indentClass}`}
          onClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            aria-label={`Select all ${rows.length} simulated conversations in this group`}
            checked={
              groupSelectionState === "all"
                ? true
                : groupSelectionState === "partial"
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={() => onToggleSelection(rows)}
          />
        </td>
      )}
      <td className={`text-sm py-2 px-3 align-middle ${indentClass}`}>
        {tooltip ? (
          <Tooltip
            content={tooltip}
            side="top"
            align="start"
            className="max-w-[min(28rem,calc(100vw-2rem))] max-h-64 overflow-auto whitespace-pre-wrap wrap-break-word p-3"
          >
            {labelNode}
          </Tooltip>
        ) : (
          labelNode
        )}
        <span className="ml-2 text-sm text-coval-text-muted">
          {rows.length} outputs
        </span>
        {assignmentChips.length > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 flex-wrap">
            {assignmentChips.map(([groupId, count]) => {
              const name = groupId
                ? (customDimensionGroupNameById?.[groupId] ?? groupId)
                : "Unassigned"
              const color = groupId
                ? customDimensionGroupColorById?.[groupId]
                : undefined
              return (
                <span
                  key={groupId ?? "__unassigned__"}
                  className={cn(
                    "inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                    !groupId &&
                      "bg-surface-0 text-coval-text-muted border border-coval-border",
                  )}
                  style={
                    color
                      ? {
                          backgroundColor: hexToRgba(color, 0.2),
                          color,
                          border: `1px solid ${hexToRgba(color, 0.5)}`,
                        }
                      : undefined
                  }
                >
                  {name} {count}
                </span>
              )
            })}
          </span>
        )}
      </td>
      {metricColumns.map((column) => {
        const metric = Object.values(metricRenderAdaptersById).find(
          (adapter) => adapter.id === column.key,
        )
        const metricOutputs = metric
          ? rows
              .map((row) => row.metric_outputs?.[metric.id])
              .filter((output): output is NonNullable<typeof output> =>
                Boolean(output?.output_type),
              )
          : []

        return (
          <td
            key={column.key}
            className="text-sm py-2 px-3 align-middle min-w-[100px] text-center"
          >
            {!metric ? (
              "—"
            ) : metricOutputs.length === 0 ? (
              <span className="text-coval-text-muted text-sm">N/A</span>
            ) : (
              <div className="flex justify-center">
                {metric.renderGroupedSimulationCard({
                  dimension: groupCompareBy.dimension,
                  metadataKey: groupCompareBy.metadataKey,
                  groupKey,
                  metricOutputs,
                  mode,
                })}
              </div>
            )}
          </td>
        )
      })}
      {Array.from({ length: explanationColumnCount }, (_, index) => (
        <td key={index} className="text-sm py-2 px-3" />
      ))}
      <td className="text-sm py-2 px-3" />
    </tr>
  )
}
